import {LogLevels} from '@pexip/logger';

/** The levels a frontend record can carry, from least to most severe. */
export type FrontendRecordLevel =
    'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/** Where a frontend record came from. */
export type FrontendRecordSource =
    | 'frontend'
    | 'console'
    | 'window-error'
    | 'unhandled-rejection'
    | 'worker-console'
    | 'worker-error'
    | 'worker-unhandled-rejection';

/**
 * One redacted logger event in the shape the native host persists. The host
 * validates and redacts it again, so this is a transport shape, not a trust
 * boundary.
 */
export interface FrontendLogRecord {
    /** Epoch milliseconds. */
    readonly timestamp: number;
    readonly level: FrontendRecordLevel;
    /** `frontend` for a logger event; the others for captured events. */
    readonly source: FrontendRecordSource;
    /** The logger name, such as `infinity` or `media`. */
    readonly target: string;
    readonly message: string;
    /** Every other field of the event. */
    readonly context: Readonly<Record<string, unknown>>;
}

const DEFAULT_TARGET = 'infinity-connect';

/**
 * Fixed bounds of one frontend record, the same the native host enforces, so
 * an oversized record is replaced before it is sent rather than only when it
 * is persisted. Not administrator-configurable.
 */
const FRONTEND_RECORD_LIMITS = Object.freeze({
    /** Nesting levels, the context itself being the first. */
    maxDepth: 8,
    /** Properties of one object. */
    maxProperties: 100,
    /** Elements of one array. */
    maxElements: 100,
    /** UTF-8 bytes of the target, the message, and every string and field name. */
    maxStringBytes: 16 * 1024,
    /** UTF-8 bytes of the serialized record, newline included. */
    maxRecordBytes: 64 * 1024,
});

/** Which of {@link FRONTEND_RECORD_LIMITS} a record exceeded. */
type RecordLimit =
    'depth' | 'properties' | 'elements' | 'string_bytes' | 'record_bytes';

const REPLACEMENT_TARGET = 'logging';
const REPLACEMENT_MESSAGE = 'A diagnostic record was dropped';
/**
 * Stands in for the timestamp when measuring a record: the host persists it
 * as a canonical string such as `2026-10-06T12:00:00.000Z`, which is longer
 * than the epoch milliseconds sent over IPC.
 */
const PERSISTED_TIMESTAMP_PLACEHOLDER = '0000-00-00T00:00:00.000Z';

const textEncoder = new TextEncoder();

/**
 * The UTF-8 length of `text` in bytes.
 *
 * @example
 * utf8Bytes('€'); // 3
 */
const utf8Bytes = (text: string) => textEncoder.encode(text).byteLength;

/**
 * Whether `text` exceeds the UTF-8 string limit.
 *
 * @example
 * isStringTooLong('a'.repeat(16 * 1024 + 1)); // true
 */
const isStringTooLong = (text: string) =>
    // A UTF-8 encoding is never shorter than the UTF-16 length.
    text.length > FRONTEND_RECORD_LIMITS.maxStringBytes ||
    utf8Bytes(text) > FRONTEND_RECORD_LIMITS.maxStringBytes;

/**
 * The first limit a context value exceeds at `depth`, in the order the native
 * host checks them, or `undefined` when it is within every limit.
 *
 * @example
 * exceededLimit({list: Array(101).fill(0)}, 1); // 'elements'
 */
const exceededLimit = (
    value: unknown,
    depth: number,
): RecordLimit | undefined => {
    const {maxDepth, maxProperties, maxElements} = FRONTEND_RECORD_LIMITS;
    if (typeof value === 'string') {
        return isStringTooLong(value) ? 'string_bytes' : undefined;
    }
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    if (depth > maxDepth) {
        return 'depth';
    }
    if (Array.isArray(value)) {
        if (value.length > maxElements) {
            return 'elements';
        }
        for (const item of value) {
            const exceeded = exceededLimit(item, depth + 1);
            if (exceeded) {
                return exceeded;
            }
        }
        return undefined;
    }
    const entries = Object.entries(value);
    if (entries.length > maxProperties) {
        return 'properties';
    }
    for (const [name, field] of entries) {
        if (isStringTooLong(name)) {
            return 'string_bytes';
        }
        const exceeded = exceededLimit(field, depth + 1);
        if (exceeded) {
            return exceeded;
        }
    }
    return undefined;
};

/**
 * The first limit `record` exceeds, or `undefined` when it fits. The record
 * size is measured as the host persists it, with the canonical timestamp.
 *
 * @example
 * exceededRecordLimit({...record, message: 'a'.repeat(16 * 1024 + 1)}); // 'string_bytes'
 */
const exceededRecordLimit = (
    record: FrontendLogRecord,
): RecordLimit | undefined => {
    if (isStringTooLong(record.target) || isStringTooLong(record.message)) {
        return 'string_bytes';
    }
    const exceeded = exceededLimit(record.context, 1);
    if (exceeded) {
        return exceeded;
    }
    const persisted = {
        ...record,
        timestamp: PERSISTED_TIMESTAMP_PLACEHOLDER,
    };
    return utf8Bytes(JSON.stringify(persisted)) + 1 >
        FRONTEND_RECORD_LIMITS.maxRecordBytes
        ? 'record_bytes'
        : undefined;
};

/**
 * `level`, raised to `warn` when it is less severe.
 *
 * @example
 * atLeastWarn('debug'); // 'warn'
 * atLeastWarn('fatal'); // 'fatal'
 */
const atLeastWarn = (level: FrontendRecordLevel): FrontendRecordLevel =>
    level === 'error' || level === 'fatal' ? level : 'warn';

/**
 * A generic record in place of one that exceeded `limit`. It keeps the
 * time, source and validated level, at least `warn`, but none of the target,
 * message, field names or values.
 *
 * @example
 * replacementRecord(record, 'depth');
 * // {..., target: 'logging', message: 'A diagnostic record was dropped',
 * //  context: {reason: 'limit_exceeded', limit: 'depth', originalLevel: 'info'}}
 */
const replacementRecord = (
    record: FrontendLogRecord,
    limit: RecordLimit,
): FrontendLogRecord => ({
    timestamp: record.timestamp,
    level: atLeastWarn(record.level),
    source: record.source,
    target: REPLACEMENT_TARGET,
    message: REPLACEMENT_MESSAGE,
    context: {reason: 'limit_exceeded', limit, originalLevel: record.level},
});

const levelsByValue = new Map<number, FrontendRecordLevel>([
    [LogLevels.trace, 'trace'],
    [LogLevels.debug, 'debug'],
    [LogLevels.info, 'info'],
    [LogLevels.warn, 'warn'],
    [LogLevels.error, 'error'],
    [LogLevels.fatal, 'fatal'],
]);

const isTimestamp = (value: unknown): value is number =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

/**
 * Maps a redacted logger event to a frontend record. A missing or unknown
 * level becomes `warn`, an unusable time becomes `now()`, and the level, time,
 * message and logger name are moved out of the context. A record that exceeds
 * {@link FRONTEND_RECORD_LIMITS} is replaced whole with a generic record that
 * names the limit, never with a prefix of it.
 *
 * @example
 * toFrontendRecord({level: 30, time: 1, name: 'media', msg: 'Started'});
 * // {timestamp: 1, level: 'info', source: 'frontend', target: 'media',
 * //  message: 'Started', context: {}}
 */
export const toFrontendRecord = (
    event: Readonly<Record<string, unknown>>,
    {
        now = Date.now,
        source = 'frontend',
    }: {now?: () => number; source?: FrontendRecordSource} = {},
): FrontendLogRecord => {
    const {level, time, name, msg, ...context} = event;

    const record: FrontendLogRecord = {
        timestamp: isTimestamp(time) ? time : now(),
        level:
            (typeof level === 'number'
                ? levelsByValue.get(level)
                : undefined) ?? 'warn',
        source,
        target: typeof name === 'string' && name !== '' ? name : DEFAULT_TARGET,
        message: typeof msg === 'string' ? msg : '',
        context,
    };
    const exceeded = exceededRecordLimit(record);
    return exceeded ? replacementRecord(record, exceeded) : record;
};
